import { observer } from "mobx-react";
import { EmailDetailsStore } from "../Stores/EmailDetailsStore";
import { useTranslation } from "react-i18next";
import { EmailSendModal, MailType } from "../EmailSendModal/EmailSendModal";
import { emailSendModal } from "../utils/emailModalProps";
import { projectsDataStore } from "Stores/ProjectsStore";
import { MAIL_LINK } from "../Communication";
import { EmailDetailsBody } from "../Components/EmailDetailsBody";
import { DetailListActions, PrimaryActionsConfig, DropdownActionConfig } from "Components/General/DetailListActions";
import { Spin } from "Components/General/Spin";
import { withModal, WithModal } from "Components/General/Modal/withModal";
import { userInfoStore } from "Stores";
import { DownloadEmailAttachments } from "../Components/DownloadEmailAttachments";
import { getUrlParams } from "utils/getUrlParams";
import { notificationError } from "utils/libHelpers/AntdNotification";
import { extractIdFromUId } from "../utils/extractIdFromUId";
import { groupListStore } from "Pages/Project/Groups/Stores/GroupsStore";
import { isEmpty } from "lodash";
import { EmailRecipientView, EmailRecipientViewMode } from "../EmailSendModal/EmailSendForm";
import { Row } from "antd";
import { EmailSubmitAction, emailSubmitStore, MailInitialRecipients } from "../Stores/EmailSubmitStore";
import { useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { I18N_NS } from "Config/i18n";
import {
  useCheckDeletedFilesAndUsers,
  EmailValidData,
  EmailInvalidData,
} from "utils/hooks/useCheckDeletedFilesAndUsers";
import { modal } from "Components/General/Modal/AntdModal";

export interface EmailDetailsProps extends WithModal {
  type: API_MAILRESPONSE_TYPE;
  projectSpecific?: boolean; // is passed from email search results page
  updateEmailData: (emailData: API_MailResponse) => void;
  refreshList?: () => Promise<void>;
  primaryActions: PrimaryActionsConfig[];
  dropdownActions?: DropdownActionConfig[];
  goBack?: () => void;
  emailId?: string; // used when showing the mail outside of the comm. module. (f.e. as linked mail from docs)
}

export const EmailDetails = withModal(
  observer(
    ({
      primaryActions,
      dropdownActions,
      type: emailType,
      projectSpecific,
      updateEmailData,
      refreshList,
      showModal,
      goBack,
      emailId,
    }: EmailDetailsProps) => {
      const emailDetailsStore = useMemo(() => new EmailDetailsStore(), []);
      const { t } = useTranslation(I18N_NS.PROJECT);
      const navigate = useNavigate();
      const params = useParams<{ id: string }>();
      const location = useLocation();
      const { checkDeletedFilesAndUsers } = useCheckDeletedFilesAndUsers(emailSubmitStore);
      const {
        to,
        from,
        cc,
        bcc,
        subject,
        timestamp,
        body,
        attachments,
        correspondents,
        id,
        type,
        projectSpecific: dataProjectSpecific,
        abstractRecipients,
      } = emailDetailsStore.data;

      const getParsedInMailGroupsArray = useCallback((data: API_AbstractRecipients) => {
        const uniqueGroupsMap = new Map();
        let groups = data.to.groups;
        groups = [...groups, ...data.cc.groups, ...data.bcc.groups];
        groups.forEach((group) => {
          const { name, members } = group;
          const id = extractIdFromUId(name).value;
          const groupMembers = members.map((v) => {
            const { type, value } = extractIdFromUId(v);
            return {
              name: type,
              id: value,
            };
          });
          if (!uniqueGroupsMap.has(name)) {
            uniqueGroupsMap.set(name, { id, groupMembers });
          }
        });
        return Array.from(uniqueGroupsMap.values());
      }, []);

      const compareGroupMembers = useCallback(
        (
          currentGroups: API_Group[],
          inMailGroups: Pick<API_Group, "id" | "groupMembers">[]
        ): { differenceInGroups: boolean; exceptedGroupId: number[] } => {
          const currentGroupsMap = new Map(currentGroups.map((group) => [group.id, group]));
          const result: { differenceInGroups: boolean; exceptedGroupId: number[] } = {
            differenceInGroups: false,
            exceptedGroupId: [],
          };
          for (const inMailGroup of inMailGroups) {
            const existedGroup = currentGroupsMap.get(inMailGroup.id);
            if (!existedGroup) {
              result.exceptedGroupId = result.exceptedGroupId.concat(inMailGroup.id);
            } else {
              if (existedGroup.nmbOfMembers !== inMailGroup.groupMembers.length) {
                result.differenceInGroups = true;
              }

              const existedMembersSet = new Set(existedGroup.groupMembers.map((member) => member.id));
              for (const inMailGroupMember of inMailGroup.groupMembers) {
                if (!existedMembersSet.has(inMailGroupMember.id)) {
                  result.differenceInGroups = true;
                }
              }
            }
          }

          return result;
        },
        []
      );

      const initEmailDetails = useCallback(async () => {
        const url = location.pathname;
        const downloadAllFiles = location ? getUrlParams(location.search).downloadAllFiles : null;
        try {
          const isProjectSpecific =
            (typeof projectSpecific === "boolean"
              ? projectSpecific
              : url.indexOf(MAIL_LINK.INBOX_PROJECT) !== -1 || url.indexOf(MAIL_LINK.SENT_PROJECT) !== -1) ||
            url.indexOf(MAIL_LINK.TOPIC_INBOX) !== -1;
          await (emailDetailsStore as EmailDetailsStore).init({
            emailType,
            emailId: params.id || emailId,
            projectSpecific: isProjectSpecific,
          });
          updateEmailData(JSON.parse(JSON.stringify(emailDetailsStore.data)));
          if (downloadAllFiles) {
            modal.confirm({
              title: t("Download all files"),
              content: t("Are you sure you want to download all files?"),
              onOk: () => {
                emailDetailsStore.downloadAllFiles();
              },
            });
          }
        } catch (error) {
          if (error.message === "NO_FILES") {
            notificationError(t("Error"), t("There are no files that can be downloaded"));
          }
          if (navigate) {
            // when error occurs - go from email details page to email list page.
            const splittedCurrentUrl = url.split("/");
            const oneLevelUp = splittedCurrentUrl.slice(0, splittedCurrentUrl.length - 1).join("/");
            navigate(oneLevelUp);
            throw error;
          }
        }
      }, [emailType, location, projectSpecific, params.id, emailId, emailDetailsStore, updateEmailData, navigate]);

      const getRecipientsView = useCallback(
        (recipients: API_AbstractMailRecipientEntry, groupView: EmailRecipientView): string[] => {
          let parsedRecipients = [];

          const forceSplitGroup = (groupName: string) => {
            const groupId = +extractIdFromUId(groupName).value;
            return groupView.exceptedGroupId.includes(groupId);
          };

          const getGroupMembers = (groups: typeof recipients.groups) =>
            groups.reduce((members, group) => {
              if (groupView.mode === EmailRecipientViewMode.BY_GROUP && !forceSplitGroup(group.name)) {
                return members.concat(group.name);
              }
              return members.concat(group.members.slice());
            }, []);

          parsedRecipients = getGroupMembers(recipients.groups);
          return parsedRecipients.concat(recipients.individuals.slice());
        },
        []
      );

      const mailSubmitCallback = useCallback(
        async (id?: string) => {
          await refreshList?.();
          if (id) {
            navigate(`/project/${projectsDataStore.projectId}/email/${MAIL_LINK[emailType]}/${id}`);
          } else {
            navigate(`/project/${projectsDataStore.projectId}/email/${MAIL_LINK[emailType]}`);
          }
        },
        [refreshList, navigate, emailType]
      );

      const replyEmail = useCallback(
        (mailData: EmailValidData, mailType: MailType = "reply_all", mailSubmitAction: EmailSubmitAction) => {
          showModal(EmailSendModal, {
            modalProps: { ...emailSendModal },
            componentProps: {
              mailData,
              mailType,
              mailSubmitAction,
              onSubmitCallback: mailType === "draft" && mailSubmitCallback,
            },
          });
        },
        [showModal, mailSubmitCallback]
      );

      const initialRecipients: MailInitialRecipients = {
        initialRecipients: {
          to: emailDetailsStore.data.to,
          cc: emailDetailsStore.data.cc,
          bcc: emailDetailsStore.data.bcc,
        },
      };

      const getMailActionType = useCallback((
        type: MailType, id: string, draftCopy: boolean = false
      ): EmailSubmitAction => {
        if (type === "draft") {
          if (draftCopy) {
            return "copy_draft";
          }
          if (id) {
            return "edit_draft";
          }
          return "save_draft";
        }
        return "sent";
      }, [type]);

      const beforeReply = useCallback(
        (type: MailType, draftCopy: boolean = false) => {
          const abstractRecipients = emailDetailsStore.data?.abstractRecipients;

          // In order to correctly render recipients in the text content in send form.
          // Since we pre-delete inactive users, we change the initial to, cc, bcc
          // and content is built on these initial values.
          // We need to optimize it in the future. For now, it's just a quick fix.

          // This is used to determine the action type for the email submit
          const mailSubmitAction = getMailActionType(type, emailDetailsStore.data.id, draftCopy);

          // We don't need getRecipientsView for "reply" action, since the recipient will be only "from"
          if (isEmpty(abstractRecipients) || type === "reply") {
            checkDeletedFilesAndUsers(emailDetailsStore.data, type).then((initialData) => {
              replyEmail({ ...initialData, ...initialRecipients }, type, mailSubmitAction);
            });
          } else {
            const mailGroups = getParsedInMailGroupsArray(abstractRecipients);
            const { differenceInGroups, exceptedGroupId } = compareGroupMembers(
              groupListStore.forMailAndPlot,
              mailGroups
            );

            const getValidInitialData = async (
              mode: EmailRecipientViewMode,
              exceptedGroupId?: number[]
            ): Promise<EmailValidData> => {
              const parsedRecipientsView: EmailInvalidData = {
                to: getRecipientsView(abstractRecipients.to, { mode, exceptedGroupId }),
                cc: getRecipientsView(abstractRecipients.cc, { mode, exceptedGroupId }),
                bcc: getRecipientsView(abstractRecipients.bcc, { mode, exceptedGroupId }),
                attachments: emailDetailsStore.data.attachments || [],
                from: emailDetailsStore.data.from,
                importance: emailDetailsStore.data.importance,
              };
              const validData = await checkDeletedFilesAndUsers(parsedRecipientsView, type);
              return {
                ...emailDetailsStore.data,
                ...initialRecipients,
                ...validData,
              };
            };

            if (differenceInGroups) {
              modal.confirm({
                title: t(`Do you want to use the updated groups?`),
                content: (
                  <div>
                    {t(
                      `The members of one or more recipient groups have changed. The recipients can either be the updated groups with the current members or the previous recipients can be listed individually.`
                    )}
                  </div>
                ),
                okText: t("Yes"),
                okType: "primary",
                cancelText: t("No"),
                zIndex: 999999,
                style: { width: "720px" },
                onOk: () => {
                  getValidInitialData(EmailRecipientViewMode.BY_GROUP, exceptedGroupId).then((initialData) => {
                    replyEmail({ ...initialData, ...initialRecipients }, type, mailSubmitAction);
                  });
                },
                onCancel: () => {
                  getValidInitialData(EmailRecipientViewMode.BY_PERSON, exceptedGroupId).then((initialData) => {
                    replyEmail({ ...initialData, ...initialRecipients }, type, mailSubmitAction);
                  });
                },
              });
            } else {
              getValidInitialData(EmailRecipientViewMode.BY_GROUP, exceptedGroupId).then((initialData) => {
                replyEmail({ ...initialData, ...initialRecipients }, type, mailSubmitAction);
              });
            }
          }
        },
        [
          emailDetailsStore.data,
          getRecipientsView,
          getParsedInMailGroupsArray,
          compareGroupMembers,
          checkDeletedFilesAndUsers,
          replyEmail,
        ]
      );

      const forwardEmail = useCallback(() => {
        checkDeletedFilesAndUsers(emailDetailsStore.data, "forward").then((initialData) => {
          showModal(EmailSendModal, {
            modalProps: { ...emailSendModal },
            componentProps: {
              mailData: { ...initialData, ...initialRecipients },
              mailType: "forward",
            },
          });
        });
      }, [emailDetailsStore.data, checkDeletedFilesAndUsers, showModal]);

      const handleDownloadFiles = useCallback(() => {
        showModal(DownloadEmailAttachments, {
          modalProps: {
            footer: null,
            maskClosable: false,
            width: `100%`,
            title: t("Download files") + "...",
            style: { maxWidth: 1200 },
          },
          componentProps: {
            mailData: { ...emailDetailsStore.data },
          },
        });
      }, [emailDetailsStore.data, showModal]);

      const onGoToFolder = useCallback(() => {
        const { linkedFolderId, linkedFileId } = emailDetailsStore.data;
        navigate(`/project/${projectsDataStore.projectId}/documents/docs/${linkedFolderId}?file=${linkedFileId}`);
      }, [emailDetailsStore.data, navigate]);

      const getEmailDraftActionsConfig = useCallback(() => {
        const transformedPrimaryActions = primaryActions.map(({ onClick, ...rest }) => ({
          ...rest,
          onClick: () => onClick(params.id || emailId),
        }));
        const baseActions = [
          {
            onClick: () => beforeReply("draft"),
            icon: ["far", "edit"],
            label: t("Edit"),
          },
          {
            // onClick: () => saveDraftCopy(),
            onClick: () => beforeReply("draft", true),
            icon: ["far", "copy"],
            label: t("Create copy"),
          },
        ];
        goBack &&
          baseActions.unshift({
            onClick: goBack,
            icon: ["far", "chevron-left"],
            label: t("Back"),
          });

        return { primaryActions: [...baseActions, ...transformedPrimaryActions] };
      }, [primaryActions, params.id, emailId, beforeReply, goBack]);

      const getEmailActionsConfig = useCallback(() => {
        if (emailType === "DRAFT") {
          return getEmailDraftActionsConfig();
        }

        let localPrimaryActions = [...primaryActions];
        let localDropdownActions = dropdownActions ? [...dropdownActions] : [];

        // Transform the actions to include the email id
        localPrimaryActions = localPrimaryActions.map(({ onClick, ...rest }) => ({
          ...rest,
          onClick: () => onClick(params.id || emailId),
        }));
        localDropdownActions = localDropdownActions.map(({ onClick, ...rest }) => ({
          ...rest,
          onClick: () => onClick(params.id || emailId),
        }));

        const forRestrictedUser = !userInfoStore.allowedToChangeSetting;
        let emailPrimaryActions: PrimaryActionsConfig[] = [];
        if (forRestrictedUser) {
          emailPrimaryActions = [
            {
              onClick: forwardEmail,
              icon: ["fas", "share"],
              label: t("Forward"),
            },
          ];
        } else {
          emailPrimaryActions = [
            {
              onClick: () => beforeReply("reply"),
              icon: ["fas", "reply"],
              label: t("Reply"),
            },
            {
              onClick: () => beforeReply("reply_all"),
              icon: ["fas", "reply-all"],
              label: t("Answer all"),
            },
            {
              onClick: forwardEmail,
              icon: ["fas", "share"],
              label: t("Forward"),
            },
          ];
        }
        if (
          emailDetailsStore.data.hasAttachments &&
          emailDetailsStore.data.attachments.some((item) => item.fileActions?.download)
        ) {
          localPrimaryActions.push({
            onClick: handleDownloadFiles,
            icon: ["far", "download"],
            label: t("Download files") + "...",
          });
        }
        localPrimaryActions.unshift(...emailPrimaryActions);
        if (goBack) {
          localPrimaryActions.unshift({
            onClick: goBack,
            icon: ["fas", "chevron-left"],
            label: t("Back"),
          });
        }

        if (!emailDetailsStore.allowedToMoveToProject) {
          localDropdownActions = localDropdownActions.filter((item) => item.key !== "move");
        }

        if (emailDetailsStore.data.linkedFolderId) {
          localDropdownActions.push({
            onClick: onGoToFolder,
            icon: "folder-open",
            label: t("Jump to directory"),
          });
        }

        return { primaryActions: localPrimaryActions, dropdownActions: localDropdownActions };
      }, [
        emailType,
        primaryActions,
        dropdownActions,
        params.id,
        emailId,
        getEmailDraftActionsConfig,
        emailDetailsStore,
        forwardEmail,
        beforeReply,
        handleDownloadFiles,
        goBack,
        onGoToFolder,
      ]);

      useEffect(() => {
        const id = params.id || emailId;
        if (id) {
          initEmailDetails();
        }
      }, [params.id, emailId, initEmailDetails]);

      return (
        <Spin spinning={emailDetailsStore.state === "LOADING"}>
          <div>
            <Row justify="space-between" align="middle">
              <div>
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore */}
                <DetailListActions {...getEmailActionsConfig()} />
              </div>
            </Row>
            <EmailDetailsBody
              id={id}
              type={type}
              projectSpecific={dataProjectSpecific}
              to={to}
              from={from}
              cc={cc}
              bcc={bcc}
              subject={subject}
              timestamp={+timestamp}
              body={body}
              abstractRecipients={abstractRecipients}
              attachments={attachments}
              correspondents={correspondents}
              moveAttachToProject={emailDetailsStore.moveAttachToProject}
              onRefresh={initEmailDetails}
            />
          </div>
        </Spin>
      );
    }
  )
);
